import React, { useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { compose } from 'react-apollo';
import forEach from 'lodash/forEach';
import omitBy from 'lodash/omitBy';
import isEmpty from 'lodash/isEmpty';
import flatten from 'lodash/flatten';
import get from 'lodash/get';
import rison from 'rison';
import {
  fetchCategories,
  resetAssessmentState,
} from 'legacy/src/components/assessment/state/actions';
import { flattenTags } from 'legacy-src/utility/ItemListShared';
import {
  createFlattenHierarchyData,
  extractTags,
  findTagById,
} from 'legacy-src/utility/tagHelpers';
import { findQBSubject } from 'legacy-src/components/learnosity/utils/helpers';
import withCourseOutlineConfig from 'chameleon-packages/units/composable/withCourseOutlineConfig';
import usePersistFilters from 'chameleon-src/components/questionBank/hooks/usePersistFilters';

const getRecursiveFilterByLabel = (tags, label) => {
  if (tags.length) {
    for (let i = 0; i < tags.length; i++) {
      if (tags[i].label === label) {
        return tags[i];
      }

      const searchedTag = getRecursiveFilterByLabel(tags[i].tags, label);

      if (searchedTag) {
        return searchedTag;
      }
    }
  }
};

const getSelectedTags = (tagTree, selectedTagsRef, hierarchyRef) => {
  const { id, parent_id: parentId, tags } = tagTree;
  if (hierarchyRef[id] && hierarchyRef[id].isSelected) {
    if (!parentId) {
      forEach(hierarchyRef[id].children, (child) => {
        selectedTagsRef.push(child.id);
      });
      if (isEmpty(hierarchyRef[id].children)) {
        selectedTagsRef.push(id);
      }
    } else {
      selectedTagsRef.push(id);
    }
  } else if (
    hierarchyRef[parentId] &&
    hierarchyRef[parentId].children[id].isSelected
  ) {
    selectedTagsRef.push(id);
  } else {
    (tags || []).forEach((tag) => {
      getSelectedTags(tag, selectedTagsRef, hierarchyRef);
    });
  }
};

export const tagsCleanUp = (tags, hierarchy) => {
  if (!isEmpty(tags)) {
    return flattenTags(tags);
  }
  let selectedTags = {};

  forEach(hierarchy.current.tree, (tag, tagCategoryId) => {
    selectedTags[tagCategoryId] = [];
    getSelectedTags(tag, selectedTags[tagCategoryId], hierarchy.current);
  });

  selectedTags = omitBy(selectedTags, isEmpty);

  return flattenTags(rison.encode_object(selectedTags));
};

export const extractChildrenFromMetadata = (metadata) => {
  let result = [];
  metadata.children.forEach((child) => {
    result.push(child.label);
    result.push(...extractChildrenFromMetadata(child));
  });
  return result;
};

/**
 * Applies filters , based on the metadata being returned bu the *courseOutline* GraphQl query
 * Filter metadata can be either 'flat' or 'hierarchy' type.
 * @returns {string} a query string
 * @memberof CreateQuiz
 */
export const applyMetadataFilters = (filters, categories) => {
  if (!categories.above_fold) {
    return '';
  }

  let tagsQuery = {};

  filters.forEach((filter) => {
    const allCategories = categories.above_fold.concat(categories.below_fold);
    let categoryMatches = allCategories.filter(
      (category) => category.description === filter.name,
    );

    // support checking for root tag using filter.category eg CSP subject
    if (filter.category) {
      categoryMatches = categoryMatches.concat(
        allCategories.filter((category) => category.label === filter.category),
      );
    }
    if (categoryMatches.length) {
      const filterId = categoryMatches[0].id;
      let tags = extractTags(categoryMatches[0]);
      let matches = [];
      if (filter.type === 'flat') {
        matches = tags.filter((tag) =>
          filter.values.map((value) => value.name).includes(tag.description),
        );
      }
      if (filter.type === 'hierarchy' && filter.children.length) {
        const metadataItems = extractChildrenFromMetadata(filter);
        matches = tags.filter((tag) => metadataItems.includes(tag.label));
        if (matches.length > 0 && matches[0].tags) {
          const moreMatches = matches.map((match) =>
            match.tags.filter((tag) => metadataItems.includes(tag.label)),
          );
          const firstTag = moreMatches[0];
          if (firstTag && firstTag.length > 0) {
            matches = flatten(moreMatches);
          }
        }
      }

      const ids = matches.map((match) => match.id);
      if (ids.length > 0) {
        tagsQuery[filterId] = ids;

        const source = allCategories.filter(
          (category) => category.description === 'Source',
        );
        if (source && source.length > 0) {
          const unitQuestions = source[0].tags.filter(
            (questionFilterTag) =>
              questionFilterTag.description === 'Unit Questions',
          )[0];
          if (unitQuestions) {
            tagsQuery[unitQuestions.category_id] = [unitQuestions.id];
          }
        }
      }
    }
  });

  return rison.encode_object(tagsQuery);
};

/**
 * Provides filter categories
 * @param {object|func} options
 * */
const AssessmentFilterProvider = (options, WrappedComponent) => {
  const FilterProvider = (props) => {
    const {
      params,
      location,
      router,
      route,
      categories,
      courseOutlineConfig = {},
      chosenEducationPeriodCode,
      userSubjects,
      userData: { me, adminSubjects, isAdmin, isCoordinator },
    } = props;
    const { query, state: locationState = {} } = location;
    const { subject, unitNumber, bigIdeaType } = params;

    const userId = get(me, 'initId', 0);
    const [{ arePersistedFiltersLoaded, redirectWithFilters }] =
      usePersistFilters({
        subject,
        userId,
        router,
      });

    // bigIdeaType
    const bigIdea = params.bigIdea || locationState.bigIdea;
    const flattenHierarchy = useRef({
      tree: {},
      parentByChild: {},
      subject,
    });

    const userSubjectsToUse =
      isAdmin || isCoordinator ? adminSubjects : userSubjects;
    const subjectInfo = findQBSubject(userSubjectsToUse, params.subject);
    const initId = !!subjectInfo && subjectInfo.initId;
    const optionParams =
      typeof options === 'function' ? options(props) : options;
    const educationPeriod = get(
      optionParams,
      'chosenEducationPeriodCode',
      chosenEducationPeriodCode,
    );

    useEffect(
      () => () => {
        props.resetAssessmentState();
      },
      [],
    );

    useEffect(() => {
      if (arePersistedFiltersLoaded && !params.tags) {
        redirectWithFilters();
      }
    }, [arePersistedFiltersLoaded, params.tags]);

    useEffect(() => {
      if (initId) {
        props.fetchCategories({
          initId,
          minimal_fields_only: true,
          subunitLabel:
            (courseOutlineConfig.data &&
              courseOutlineConfig.data.subunitLabel) ||
            '',
          educationPeriod,
        });
      }
    }, [initId, educationPeriod]);

    useEffect(() => {
      //@todo - check bug: on each visit of QB/All Questions page: the
      //https://apc-api.test:9091/fym/tagging/api/subject/10133/categories/?minimal_fields_only=true&educationPeriod=23
      //is called - the results should be cached
      if (!isEmpty(categories)) {
        //Backwards compatibility for refresh page
        const filterParams = query.tags;
        if (filterParams) {
          createFlattenHierarchyData(
            categories,
            flattenHierarchy.current,
            filterParams,
          );
        }
      }
    }, [categories]);

    const generateUnitTagsString = (unitNumberValue) => {
      if (!isEmpty(categories)) {
        const above_fold = categories.above_fold || [];
        const unitFilterCategories = [
          'Unit',
          'Unit and Topic',
          'Big Idea',
          'Big Idea and Learning Objective',
          'Course Content',
        ];
        const unitFilter = above_fold.find(({ label }) =>
          unitFilterCategories.includes(label),
        );

        const firstSubCategoryTags =
          (unitFilter && unitFilter.tags && unitFilter.tags[0]) || null;
        let unitTag;
        if (
          firstSubCategoryTags &&
          unitFilterCategories.includes(firstSubCategoryTags.label)
        ) {
          unitTag = firstSubCategoryTags.tags.find(
            ({ label }) => label === unitNumberValue,
          );
        } else {
          unitTag = unitFilter.tags.find(
            ({ label }) => label === unitNumberValue,
          );
        }
        if (unitTag) {
          return `'${unitFilter.id}':!(${unitTag.id})`;
        }
      }
      return '';
    };

    if (!subjectInfo || isEmpty(categories)) {
      return null;
    }

    if (unitNumber) {
      const tagsString = generateUnitTagsString(unitNumber);
      if (tagsString) {
        const filterQueryString = `?tags=${tagsString}`;
        router.replace(
          `/${subject}/question_bank/questions${filterQueryString}`,
        );
      }
      return null;
    } else if (bigIdea) {
      const above_fold = categories.above_fold || [];
      const bigIdeaFilterCategories = [
        'Big Idea',
        'Big Idea and Learning Objective',
      ];
      const bigIdeaFilter = above_fold.find(({ label }) =>
        bigIdeaFilterCategories.includes(label),
      );

      const firstSubCategoryTags =
        (bigIdeaFilter && bigIdeaFilter.tags && bigIdeaFilter.tags[0]) || null;
      let bigIdeaTag;
      if (
        firstSubCategoryTags &&
        bigIdeaFilterCategories.includes(firstSubCategoryTags.label)
      ) {
        bigIdeaTag = firstSubCategoryTags.tags[bigIdea - 1];
      } else {
        bigIdeaTag = bigIdeaFilter && bigIdeaFilter.tags[bigIdea - 1];
      }

      if (bigIdeaTag) {
        let tagsString = `'${bigIdeaFilter.id}':!(${bigIdeaTag.id})`;

        const assessmentPurposeAndSourceCategories = [
          'Assessment Purpose and Source',
        ];
        const assessmentPurposeAndSourceFilter = above_fold.find(({ label }) =>
          assessmentPurposeAndSourceCategories.includes(label),
        );

        /*
         * Since the PERFORMANCE TASK FILTER was implemented before BIG IDEA ONLY
         * we've kept the url as /:bigIdea/big_idea for BIG IDEA + PERFORMANCE TASK.
         * Therefore the bigIdeaType will be undefined when we have to also set the PERFORMANCE TASK filter.
         * */
        const withPerformanceTaskFilter = !bigIdeaType;
        if (withPerformanceTaskFilter) {
          const performanceTaskFilter = getRecursiveFilterByLabel(
            assessmentPurposeAndSourceFilter.tags,
            'Performance Task',
          );

          const apQuestionFilter = getRecursiveFilterByLabel(
            assessmentPurposeAndSourceFilter.tags,
            'AP Topic Question',
          );
          if (performanceTaskFilter || apQuestionFilter) {
            let siblingFilter = '';
            if (apQuestionFilter.id) {
              siblingFilter = `,${apQuestionFilter.id}`;
            }
            tagsString += `,'${assessmentPurposeAndSourceFilter.id}':!(${performanceTaskFilter.id}${siblingFilter})`;
          }
        }

        const filterQueryString = `?tags=${tagsString}`;
        router.replace(
          `/${subject}/question_bank/questions${filterQueryString}`,
        );
      }
    } else if (location.pathname.includes('/filters')) {
      let filterParams = '';
      const {
        state: {
          tagId,
          topicQuestionsFilter,
          unitNumber: unitNumberFromState,
          questionBankPath,
          ...otherLocationState
        },
      } = location;
      if (tagId) {
        filterParams = findTagById(categories, tagId);
      }
      if (topicQuestionsFilter) {
        filterParams = applyMetadataFilters(topicQuestionsFilter, categories);
      }

      createFlattenHierarchyData(
        categories,
        flattenHierarchy.current,
        filterParams,
      );
      const innerPath =
        route.name === 'createQuizAutoBuildWrapper' ? '/auto-build' : '';
      const { searchTerm, sortTagsBy } = query;
      if (unitNumberFromState) {
        const tagsString = generateUnitTagsString(unitNumberFromState);
        filterParams = `${filterParams},${tagsString}`;
      }
      const urlToPath = questionBankPath || '/create';
      router.replace({
        pathname: `/${subject}/question_bank${urlToPath}${innerPath}`,
        query: {
          tags: filterParams,
          searchTerm,
          sortTagsBy,
        },
        state: otherLocationState,
      });
    }

    return (
      <WrappedComponent
        key={subject}
        {...props}
        flattenHierarchy={flattenHierarchy}
        tagsCleanUp={tagsCleanUp}
        subjectInfo={subjectInfo}
      />
    );
  };

  FilterProvider.displayName = 'FilterProvider';

  return compose(
    connect(
      (state) => ({
        categories: state.assessment.categories,
      }),
      {
        fetchCategories,
        resetAssessmentState,
      },
    ),
    withCourseOutlineConfig(),
  )(FilterProvider);
};

/**
 * Find and apply custom label
 * @param {object} filters List of filters
 * @param {string} subunitLabel The subunit configured label
 * @return {object} List of filters and if label has been found
 * */
const findAndApply = (filters, subunitLabel) => {
  const filterLabel = subunitLabel || 'Subunit';
  let labelFound = false;

  filters.map((filter) => {
    if (filter.title === 'Subunit') {
      labelFound = true;
      filter.title = filterLabel;
      filter.description = filterLabel;
    }

    return filter;
  });

  return {
    list: filters,
    labelFound,
  };
};

/**
 * Apply course outline configuration label
 * @param {object} categories List of filters
 * @param {string} subunitLabel The subunit configured label
 * @return {object} categories List of filters with custom labels applied
 * */
const applyFilterCustomLabel = (categories, subunitLabel) => {
  let labelFound = false;
  if (categories && categories.above_fold && categories.above_fold.length) {
    const filters = findAndApply(categories.above_fold, subunitLabel);
    categories.above_fold = filters.list;
    labelFound = filters.labelFound;
  }

  if (
    !labelFound &&
    categories &&
    categories.below_fold &&
    categories.below_fold.length
  ) {
    const filters = findAndApply(categories.below_fold, subunitLabel);
    categories.below_fold = filters.list;
  }

  return categories;
};

export default AssessmentFilterProvider;
export { applyFilterCustomLabel };
